// Backups page — manual snapshots of CRM/videos/etc to Cloudinary.
// Critical safety net: production deploys have rolled back Mongo data before;
// this lets the user take a snapshot before every deploy and restore from any
// past snapshot via merge (non-destructive by default).
import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Database, Download, Upload, RefreshCw, ShieldCheck, AlertTriangle, FileJson, Clock } from "lucide-react";

export default function Backups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taking, setTaking] = useState(false);
  const [lastSnapshot, setLastSnapshot] = useState(null);
  const [restoreUrl, setRestoreUrl] = useState("");
  const [restorePlan, setRestorePlan] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/admin/backups");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load backups");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const takeBackup = async () => {
    if (!window.confirm("Take a full snapshot of all data and upload to Cloudinary?\n\nRecommended before every Production Deploy.")) return;
    setTaking(true);
    try {
      const r = await api.post("/api/admin/backup");
      setLastSnapshot(r.data);
      toast.success(`Snapshot taken · ${r.data.total_docs} docs across ${Object.keys(r.data.counts).length} collections`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally { setTaking(false); }
  };

  const previewRestore = async () => {
    if (!restoreUrl.trim()) { toast.error("Pick a snapshot first"); return; }
    setRestoring(true);
    try {
      const r = await api.post("/api/admin/restore", { backup_url: restoreUrl, dry_run: true });
      setRestorePlan(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setRestoring(false); }
  };

  const confirmRestore = async () => {
    const totalInsert = Object.values(restorePlan?.plan || {}).reduce((a, p) => a + (p.would_insert || 0), 0);
    if (!window.confirm(`Restore (MERGE mode) ${totalInsert} missing document(s) from snapshot taken on ${restorePlan?.snapshot_taken_at}?\n\nNothing currently in DB will be deleted. Only missing docs (by id) will be re-inserted.`)) return;
    setRestoring(true);
    try {
      const r = await api.post("/api/admin/restore", { backup_url: restoreUrl, mode: "merge", dry_run: false });
      const tot = Object.values(r.data.applied || {}).reduce((a, c) => a + (c.inserted || 0), 0);
      toast.success(`Restored · ${tot} document(s) inserted`);
      setRestoreUrl("");
      setRestorePlan(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setRestoring(false); }
  };

  return (
    <AppLayout title="Backups & restore" overline="🛡️ DATA SAFETY · SNAPSHOT BEFORE EVERY PRODUCTION DEPLOY">
      {/* Hero / take backup */}
      <section className="rounded-2xl p-6 mb-6 border" data-testid="backups-hero"
        style={{
          background: "linear-gradient(135deg, var(--gio-primary-soft) 0%, transparent 100%)",
          borderColor: "var(--gio-primary)",
        }}>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "var(--gio-primary)", color: "#fff" }}>
            <ShieldCheck size={28} />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-2xl mb-1">Snapshot now</h2>
            <p className="text-[13px] mb-4" style={{ color: "var(--gio-muted)" }}>
              Exports CRM, videos, studios, social posts, finance, expenses, schedulers and more to a JSON file stored on your Cloudinary (folder <code>backups/gioagent</code>). Use this before every production deploy and after any big editing session.
            </p>
            <div className="flex gap-3 flex-wrap">
              <button onClick={takeBackup} disabled={taking}
                data-testid="backup-now-btn"
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                {taking ? <RefreshCw size={14} className="animate-spin" /> : <Database size={14} />}
                {taking ? "Snapshotting…" : "Backup now"}
              </button>
              <button onClick={load} className="btn-ghost inline-flex items-center gap-2" data-testid="backups-refresh">
                <RefreshCw size={14} /> Refresh list
              </button>
            </div>
            {lastSnapshot && (
              <div className="mt-4 p-3 rounded border text-[12px]" data-testid="last-snapshot-info"
                style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                <div className="overline mb-1.5">LAST SNAPSHOT · {new Date(lastSnapshot.snapshot.taken_at).toLocaleString()}</div>
                <div className="flex gap-4 flex-wrap" style={{ color: "var(--gio-muted)" }}>
                  <span>📦 {lastSnapshot.total_docs} docs</span>
                  <span>💾 {(lastSnapshot.snapshot.bytes / 1024).toFixed(1)} KB</span>
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {Object.entries(lastSnapshot.counts).filter(([, c]) => c > 0).map(([k, c]) => (
                    <span key={k} className="text-[10px] font-mono-ui">
                      <span style={{ color: "var(--gio-text)" }}>{k}</span>
                      <span style={{ color: "var(--gio-muted-3)" }}> · {c}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Snapshots list */}
      <section className="mb-6">
        <div className="overline mb-3 flex items-center gap-2">
          <FileJson size={12} /> AVAILABLE SNAPSHOTS · {items.length}
        </div>
        {loading ? (
          <div className="text-[13px]" style={{ color: "var(--gio-muted-2)" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border p-8 text-center" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
            <Database size={24} className="mx-auto mb-2" style={{ color: "var(--gio-muted-3)" }} />
            <div className="text-[13px]" style={{ color: "var(--gio-muted-2)" }}>
              No snapshots yet. Click "Backup now" above to take your first one.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(it => (
              <div key={it.public_id}
                data-testid={`snapshot-${it.public_id.replace(/[^a-z0-9]/gi, "_")}`}
                className="flex items-center gap-3 p-3 rounded border"
                style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                <div className="w-10 h-10 rounded flex items-center justify-center shrink-0"
                  style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
                  <FileJson size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold truncate" style={{ color: "var(--gio-text)" }}>
                    {it.public_id.split("/").pop()}
                  </div>
                  <div className="text-[11px] mt-0.5 inline-flex items-center gap-2" style={{ color: "var(--gio-muted-2)" }}>
                    <Clock size={10} />
                    <span>{new Date(it.created_at).toLocaleString()}</span>
                    <span>·</span>
                    <span>{(it.bytes / 1024).toFixed(1)} KB</span>
                  </div>
                </div>
                <a href={it.secure_url} target="_blank" rel="noreferrer" download
                  data-testid={`snapshot-download-${it.public_id.replace(/[^a-z0-9]/gi, "_")}`}
                  className="btn-ghost text-[11px] inline-flex items-center gap-1.5">
                  <Download size={11} /> Download
                </a>
                <button onClick={() => { setRestoreUrl(it.secure_url); setRestorePlan(null); }}
                  data-testid={`snapshot-pick-restore-${it.public_id.replace(/[^a-z0-9]/gi, "_")}`}
                  className="btn-primary text-[11px] inline-flex items-center gap-1.5">
                  <Upload size={11} /> Use for restore
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Restore block */}
      <section className="rounded-2xl p-6 border" data-testid="restore-block"
        style={{ borderColor: "rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.04)" }}>
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle size={20} style={{ color: "#fbbf24" }} className="shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading text-lg" style={{ color: "#fbbf24" }}>Restore from snapshot</h3>
            <p className="text-[12px] mt-1" style={{ color: "var(--gio-muted)" }}>
              <strong>MERGE mode</strong> — only re-inserts documents that are missing from the current DB (matched by <code>id</code>). Existing documents are NEVER overwritten or deleted.
            </p>
          </div>
        </div>
        <div className="flex gap-2 mb-3">
          <input value={restoreUrl}
            onChange={(e) => { setRestoreUrl(e.target.value); setRestorePlan(null); }}
            placeholder="Paste snapshot URL or click 'Use for restore' on a snapshot above"
            data-testid="restore-url-input"
            className="gio-input flex-1" />
          <button onClick={previewRestore} disabled={restoring || !restoreUrl.trim()}
            data-testid="restore-preview-btn"
            className="btn-ghost text-xs">
            {restoring && !restorePlan ? "Loading…" : "Preview"}
          </button>
        </div>

        {restorePlan && (
          <div className="rounded border p-3" data-testid="restore-plan"
            style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
            <div className="overline mb-2">
              RESTORE PLAN · Snapshot from {new Date(restorePlan.snapshot_taken_at).toLocaleString()}
            </div>
            <table className="w-full text-[11px]">
              <thead>
                <tr style={{ color: "var(--gio-muted-3)" }}>
                  <th className="text-left p-1.5">Collection</th>
                  <th className="text-right p-1.5">In snapshot</th>
                  <th className="text-right p-1.5">In DB now</th>
                  <th className="text-right p-1.5">Will re-insert</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(restorePlan.plan).map(([col, p]) => (
                  <tr key={col} className="border-t" style={{ borderColor: "var(--gio-border)" }}>
                    <td className="p-1.5 font-mono-ui" style={{ color: "var(--gio-text)" }}>{col}</td>
                    <td className="p-1.5 text-right">{p.in_snapshot}</td>
                    <td className="p-1.5 text-right">{p.in_db}</td>
                    <td className="p-1.5 text-right font-bold" style={{ color: p.would_insert > 0 ? "var(--gio-primary)" : "var(--gio-muted-3)" }}>
                      {p.would_insert > 0 ? `+${p.would_insert}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 flex justify-end">
              <button onClick={confirmRestore} disabled={restoring}
                data-testid="restore-confirm-btn"
                className="btn-primary inline-flex items-center gap-2">
                <Upload size={12} /> {restoring ? "Restoring…" : "Confirm restore (merge)"}
              </button>
            </div>
          </div>
        )}
      </section>
    </AppLayout>
  );
}
